import type { CustomerOrder } from '../../customerOrdersApi'
import type { FilteredOrderRow } from './ordersListSectionTypes'
import {
  parseMoneyInput,
  parseTimestamp,
  resolveVisualStatus,
  toDatePresetThreshold,
  toEndOfDayTimestamp,
  toStartOfDayTimestamp,
  type OrderDateFilterOption,
  type OrderSortOption,
  type OrderVisualStatus,
} from './ordersListShared'

type ComputeFilteredOrdersInput = {
  orders: CustomerOrder[]
  searchValue: string
  priceMin: string
  priceMax: string
  dateFilter: OrderDateFilterOption
  customDateFrom: string
  customDateTo: string
  sortBy: OrderSortOption
  statusFilters: OrderVisualStatus[]
}

export const computeFilteredOrders = ({
  orders,
  searchValue,
  priceMin,
  priceMax,
  dateFilter,
  customDateFrom,
  customDateTo,
  sortBy,
  statusFilters,
}: ComputeFilteredOrdersInput): FilteredOrderRow[] => {
  const query = searchValue.trim().toLowerCase()
  const minPrice = parseMoneyInput(priceMin)
  const maxPrice = parseMoneyInput(priceMax)
  const nowTimestamp = Date.now()
  const dateThreshold = toDatePresetThreshold(dateFilter, nowTimestamp)
  const customFromTimestamp = toStartOfDayTimestamp(customDateFrom)
  const customToTimestamp = toEndOfDayTimestamp(customDateTo)

  return orders
    .map((order) => {
      const createdTimestamp = parseTimestamp(order.createdAt)
      const visualStatus = resolveVisualStatus(order, nowTimestamp)
      return {
        order,
        createdTimestamp,
        visualStatus,
      }
    })
    .filter(({ order, createdTimestamp, visualStatus }) => {
      if (statusFilters.length > 0 && !statusFilters.includes(visualStatus)) return false

      if (query) {
        const matchesOrderNumber = order.orderNumber.toLowerCase().includes(query)
        const matchesOrderId = order.id.toLowerCase().includes(query)
        const matchesProductName = order.items.some((item) => item.productName.toLowerCase().includes(query))
        if (!matchesOrderNumber && !matchesOrderId && !matchesProductName) return false
      }

      if (dateFilter === 'custom') {
        if (customFromTimestamp !== null && createdTimestamp < customFromTimestamp) return false
        if (customToTimestamp !== null && createdTimestamp > customToTimestamp) return false
      } else if (dateThreshold !== null && createdTimestamp < dateThreshold) {
        return false
      }

      if (minPrice !== null && order.total < minPrice) return false
      if (maxPrice !== null && order.total > maxPrice) return false

      return true
    })
    .sort((left, right) => {
      if (sortBy === 'recent-desc') return right.createdTimestamp - left.createdTimestamp
      if (sortBy === 'recent-asc') return left.createdTimestamp - right.createdTimestamp
      if (sortBy === 'price-desc') {
        if (right.order.total === left.order.total) return right.createdTimestamp - left.createdTimestamp
        return right.order.total - left.order.total
      }
      if (right.order.total === left.order.total) return left.createdTimestamp - right.createdTimestamp
      return left.order.total - right.order.total
    })
}
